import type {
  ActionType,
  ChatInputAnswer,
  ChatInputAnswerState,
  ChatInputAnswerValue,
  ChatInputAnswerValueKind,
  ChatInputCompletedAction,
} from '@microsoft/agent-host-protocol'

// AHP enums are const — emit wire literals cast to their enum types
// (isolatedModules forbids importing const enums as runtime values).
const ACTION_INPUT_COMPLETED = 'chat/inputCompleted' as ActionType.ChatInputCompleted

const ANSWER_STATE_SUBMITTED = 'submitted' as ChatInputAnswerState.Submitted
const ANSWER_STATE_SKIPPED = 'skipped' as ChatInputAnswerState.Skipped

const VALUE_KIND_TEXT = 'text' as ChatInputAnswerValueKind.Text
const VALUE_KIND_SELECTED = 'selected' as ChatInputAnswerValueKind.Selected
const VALUE_KIND_SELECTED_MANY = 'selected-many' as ChatInputAnswerValueKind.SelectedMany

/** How a client completed an input request (`chat/inputCompleted.response`). */
export type InputCompletionResponse = 'accept' | 'decline' | 'cancel'

/** A submitted single-select answer, with optional freeform text override. */
export function selectedAnswer(value: string, freeformValues?: string[]): ChatInputAnswer {
  const answerValue: ChatInputAnswerValue = {
    kind: VALUE_KIND_SELECTED,
    value,
    ...(freeformValues && freeformValues.length > 0 ? {freeformValues} : {}),
  }
  return {state: ANSWER_STATE_SUBMITTED, value: answerValue}
}

/** A submitted multi-select answer, with optional freeform additions. */
export function selectedManyAnswer(values: string[], freeformValues?: string[]): ChatInputAnswer {
  const answerValue: ChatInputAnswerValue = {
    kind: VALUE_KIND_SELECTED_MANY,
    value: values,
    ...(freeformValues && freeformValues.length > 0 ? {freeformValues} : {}),
  }
  return {state: ANSWER_STATE_SUBMITTED, value: answerValue}
}

/** A submitted free-text answer. */
export function textAnswer(value: string): ChatInputAnswer {
  return {state: ANSWER_STATE_SUBMITTED, value: {kind: VALUE_KIND_TEXT, value}}
}

/** A skipped answer, optionally carrying any freeform text captured while skipping. */
export function skippedAnswer(freeformValues?: string[]): ChatInputAnswer {
  return {
    state: ANSWER_STATE_SKIPPED,
    ...(freeformValues && freeformValues.length > 0 ? {freeformValues} : {}),
  }
}

export interface BuildInputCompletedActionParams {
  requestId: string
  response: InputCompletionResponse
  /** Final answers keyed by question id. Usually omitted for decline/cancel. */
  answers?: Record<string, ChatInputAnswer>
}

/**
 * Build a client-dispatchable `chat/inputCompleted` action that accepts,
 * declines, or cancels a pending {@link ChatInputRequest}.
 */
export function buildInputCompletedAction({
  requestId,
  response,
  answers,
}: BuildInputCompletedActionParams): ChatInputCompletedAction {
  return {
    type: ACTION_INPUT_COMPLETED,
    requestId,
    response: response as ChatInputCompletedAction['response'],
    ...(answers ? {answers} : {}),
  }
}
